import React, { useEffect, useState } from 'react';
import { Button, Layout, Menu, type MenuProps, theme } from 'antd';
import {
  UserOutlined,
  RightOutlined,
  LeftOutlined,
  DollarOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  AuditOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  AppstoreAddOutlined, SlidersOutlined, SettingOutlined, ToolOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import './new-menu.css';
import { Brand, Isotype } from '@orbita-ui/core';
import { RealmRoles } from '../../constants';
import useAuthService from '../../hooks/useAuthService';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const { Header, Sider, Content } = Layout;


type MenuItem = Required<MenuProps>['items'][number];


export type PrepaidMenuItem  = MenuItem & {
  key?: React.Key;
  icon?: React.ReactNode;
  target?: string;
  label: React.ReactNode;
  children?: PrepaidMenuItem[];
  roles?: RealmRoles[];
}


function getItem(
  label: React.ReactNode,
  target?: string,
  key?: React.Key,
  icon?: React.ReactNode,
  children?: PrepaidMenuItem[],
  roles?: RealmRoles[]
): PrepaidMenuItem {
  return {
    key,
    icon,
    target,
    children,
    label,
    roles,
  };
}


const items: PrepaidMenuItem[] = [
  getItem('Usuarios', '/', '1', <UsergroupAddOutlined style={{ fontSize: '20px' }} />, [
    getItem('Titulares de Tarjeta', '/card-holders', '2', <UserOutlined />, null, [
      RealmRoles.CardHolderAllRead
    ]),
    getItem('Innominadas', '/unnamed-lots', '3', <CreditCardOutlined />, null, [
      RealmRoles.CardsUnnamedRead,
      RealmRoles.CardsUnnamedWrite,
    ]),
    getItem('Altas Masivas', '/bulk-load', '4', <UsergroupAddOutlined />, null, [
      RealmRoles.BulkLoadWrite,
      RealmRoles.BulkLoadRead,
    ]),
  ], []),

  getItem('Producto', '/card-products', '5', <AppstoreOutlined style={{ fontSize: '20px' }} />, [
    getItem('Card Products', '/card-products', '6', <AuditOutlined />, null, [
      RealmRoles.CardProductRead,
      RealmRoles.CardProductWrite,
    ]),
    getItem('Grupos de afinidad', '/card-affinity-groups', '7', <AppstoreAddOutlined />, null, [
      RealmRoles.CardAffinityGroupRead,
      RealmRoles.CardAffinityGroupWrite,
    ]),
    getItem('Limitadores de uso', '/usage-limits', '8', <SlidersOutlined />, null, [
      RealmRoles.UsageLimitsRead,
      RealmRoles.UsageLimitsWrite,
    ]),
    getItem('Cambio de Divisas', '/currency-exchange', '9', <DollarOutlined />, null, [
      RealmRoles.CurrencyExchangeAllRead,
    ])
  ], []),

  getItem('Gestión', '/', '10', <DollarOutlined style={{ fontSize: '20px' }} />, [
    getItem('Dashboard', '/dashboard', '11', <BarChartOutlined />, null, [
      RealmRoles.DashboardRead
    ]),
    getItem('Reclamos', '/claims', '12', <ToolOutlined />, null, [
      RealmRoles.TransactionClaimsRead,
      RealmRoles.TransactionClaimsWrite,
      RealmRoles.TransactionClaimsManagement
    ]),
    getItem('Settlement', '/settlement', '13', <FileProtectOutlined />, null, [
      RealmRoles.BalanceSettlementRead
    ]),
    getItem('Exportador', '/exporter', '14', <FileTextOutlined />, null, [
      RealmRoles.ExporterAllRead
    ]),
  ], []),

  getItem('Configuraciones', '/', '15', <SettingOutlined style={{ fontSize: '20px' }} />, [
    getItem('Usuarios y grupos', '/settings', '16', <SettingOutlined />, null, [
      RealmRoles.UsersAllRead,
      RealmRoles.GroupsAllRead,
      RealmRoles.ContactCenter,
      RealmRoles.RiskManagement,
      RealmRoles.IssuerImpersonate
    ]),
  ], [])
];

const NewMenu = () => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const location = useLocation();
  const { userHasRole } = useAuthService();

  const getRoles = (menu: PrepaidMenuItem) => {
      var roles = menu?.roles ?? []

      const childRoles = menu.children
        ?.map((child) => child.roles || [])
        .flat() || [];

      return roles.concat(childRoles).filter((role, index, self) => self.indexOf(role) === index)
  }

  var filteredItems = items?.filter((item) => userHasRole(getRoles(item)));

  filteredItems.forEach((item) => {
    item.children = (item.children?.filter((child) => userHasRole(child?.roles)) ?? []) as PrepaidMenuItem[];
  });


  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [selectedKey, setSelectedKey] = useState<string[]>();

  useEffect(() => {
    // @ts-ignore
    setSelectedKey(filteredItems?.map(subItem => subItem?.children?.find((item) => location.pathname.includes(item.target))?.key));
  }, []);

  const handleMenuClick = ({ key }: { key: string }) => {
    const { target } = filteredItems?.flatMap((subItem) => subItem?.children || []).find((item) => item?.key === key);
    if (target) {
      setSelectedKey([key]);
      navigate(`/${issuer}${target}`);
    }
  };


  return (
    <Layout
      className={'layout-ant-menu'}
      style={ collapsed ? { width: '80px', maxWidth: '80px' } : { width: '256px', paddingBlock: 0, maxWidth: '256px' } }
    >
      <Sider
        width={ 256 }
        theme="light"
        trigger={null}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
      >
        <div style={{ paddingTop: '0.875rem', paddingLeft: '1rem', paddingRight: '1rem', minHeight: '5rem' }}>
          {collapsed ? (
            <Isotype style={{ width: '100%', height: '100%' }} />
          ) : (
            <Brand style={{ width: '100%', height: '100%' }} />
          )}
        </div>
        <Menu
          defaultSelectedKeys={selectedKey}
          defaultOpenKeys={['1', '5', '11', '14']}
          selectedKeys={selectedKey}
          mode="inline"
          items={filteredItems}
          onClick={handleMenuClick}
          style={{ marginTop: '2rem' }}
        />
      </Sider>
      <Header style={ collapsed ? { paddingLeft: '75px', background: 'transparent', position: 'fixed' } : { paddingLeft: '255px', background: 'transparent', position: 'fixed' }}>
        <Button
          type="text"
          icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          className={'arrow-collapsed'}
        />
      </Header>
    </Layout>
  )
}

export default NewMenu;